import { z } from "zod";

export const serUint8ArraySchema = z.object({
  $type: z.literal("Uint8Array"),
  data: z.instanceof(ArrayBuffer),
});
type SerUint8Array = z.infer<typeof serUint8ArraySchema>;

export const serUint16ArraySchema = z.object({
  $type: z.literal("Uint16Array"),
  data: z.instanceof(ArrayBuffer),
});
type SerUint16Array = z.infer<typeof serUint16ArraySchema>;

export const serFloat32ArraySchema = z.object({
  $type: z.literal("Float32Array"),
  data: z.instanceof(ArrayBuffer),
});
type SerFloat32Array = z.infer<typeof serFloat32ArraySchema>;

const serArraySchema = z.discriminatedUnion("$type", [
  serUint8ArraySchema,
  serUint16ArraySchema,
  serFloat32ArraySchema,
]);
type SerArray = z.infer<typeof serArraySchema>;

const miscValueSchema = z.union([z.string(), z.number()]);
type MiscValue = z.infer<typeof miscValueSchema>;

export const serArrayRecordSchema = z.record(
  z.string(),
  serArraySchema.or(miscValueSchema)
);
type SerArrayRecord = z.infer<typeof serArrayRecordSchema>;

const parsableArraySchema = z.union([
  z.instanceof(Uint8Array),
  z.instanceof(Uint16Array),
  z.instanceof(Float32Array),
]);
type ParsableArray = z.infer<typeof parsableArraySchema>;

const parsableArrayRecordSchema = z.record(
  z.string(),
  parsableArraySchema.or(miscValueSchema)
);
type ParsableArrayRecord = z.infer<typeof parsableArrayRecordSchema>;

function isParsableArray(data: unknown): data is ParsableArray {
  return (
    data instanceof Uint8Array ||
    data instanceof Uint16Array ||
    data instanceof Float32Array
  );
}

function serializeArray(data: ParsableArray): SerArray {
  if (data instanceof Uint8Array) {
    return { $type: "Uint8Array", data: data.buffer };
  }
  if (data instanceof Uint16Array) {
    return { $type: "Uint16Array", data: data.buffer };
  }
  if (data instanceof Float32Array) {
    return { $type: "Float32Array", data: data.buffer };
  }
  throw new Error("unreachable");
}

type TestSerArrayRecord<T> = {
  [K in keyof T]: T[K] extends Uint8Array
    ? SerUint8Array
    : T[K] extends Uint16Array
    ? SerUint16Array
    : T[K] extends Float32Array
    ? SerFloat32Array
    : T[K] extends MiscValue
    ? T[K]
    : never;
};

function serializeArrayRecord<
  T extends { [key: string]: ParsableArray | MiscValue }
>(data: ParsableArrayRecord): TestSerArrayRecord<T> {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => {
      if (isParsableArray(value)) {
        return [key, serializeArray(value)];
      }
      return [key, value];
    })
  );
}

function deserializeArray(data: SerArray): ParsableArray {
  switch (data.$type) {
    case "Uint8Array":
      return new Uint8Array(data.data);
    case "Uint16Array":
      return new Uint16Array(data.data);
    case "Float32Array":
      return new Float32Array(data.data);
  }
}

type TestParsableArrayRecord<T> = {
  [K in keyof T]: T[K] extends ParsableArray
    ? T[K]
    : T[K] extends MiscValue
    ? T[K]
    : never;
};

function deserializeArrayRecord<
  T extends { [key: string]: ParsableArray | MiscValue }
>(data: SerArrayRecord): TestParsableArrayRecord<T> {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => {
      if (typeof value === "object" && "$type" in value) {
        return [key, deserializeArray(value)];
      }
      return [key, value];
    })
  );
}

export { serializeArrayRecord, deserializeArrayRecord };
